import React from "react";
import { Map, Loader2 } from "lucide-react";
import { useTargeting } from "./TargetingContext";

// Centred icon + title + helper used by the empty and (fallback) ready states.
const MapMessage = ({
  icon,
  title,
  body,
  ...props
}: { icon: React.ReactNode; title: string; body?: string } & React.HTMLAttributes<HTMLDivElement>) => (
  <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center" {...props}>
    <div className="flex size-12 items-center justify-center rounded-xl bg-muted">{icon}</div>
    <div className="flex max-w-[480px] flex-col gap-2">
      <p className="text-base font-medium text-foreground">{title}</p>
      {body && <p className="text-sm text-muted-foreground">{body}</p>}
    </div>
  </div>
);

// The map region of the Targeting card. Owns the empty (no city) and loading
// (AC19) states; once a city is selected and recommendations have loaded, it
// renders the channel-specific map passed as children (H2H pins = F4, D2D
// polygons = F5). With no children it shows a neutral ready placeholder.
export const MapContainer = ({ children }: { children?: React.ReactNode }) => {
  const { selectedCity, isAreasLoading } = useTargeting();

  if (!selectedCity) {
    return (
      <div
        className="h-[400px] w-full rounded-md border border-dashed border-slate-400 dark:border-slate-500"
        data-cypress-id="targeting-map-empty"
      >
        <MapMessage
          icon={<Map className="size-6 text-muted-foreground" />}
          title="Select a city"
          body="After selecting the city, you will be able to choose specific distribution areas in the map."
          data-cypress-id="cityTextInBox"
        />
      </div>
    );
  }

  if (isAreasLoading) {
    return (
      <div
        className="h-[400px] w-full rounded-md border border-dashed border-slate-400 dark:border-slate-500"
        data-cypress-id="targeting-map-loading"
      >
        <MapMessage
          icon={<Loader2 className="size-6 animate-spin text-muted-foreground" />}
          title="Loading distribution areas"
          body="Fetching the recommended areas for your city."
        />
      </div>
    );
  }

  return (
    <div
      className="h-[400px] w-full overflow-hidden rounded-md border border-solid border-border"
      data-cypress-id="targeting-map"
    >
      {children ?? <MapMessage icon={<Map className="size-6 text-muted-foreground" />} title="Map" />}
    </div>
  );
};
